import { SVGProps, forwardRef } from 'react'
import type { ComponentProps, ElementRef } from 'react'

import type { CSSProps, VariantProps } from 'style'

import ButtonStyled, {
  ButtonCenterElement,
  ButtonIcon,
  ButtonLeftElement,
  ButtonLoadingLabel,
} from 'components/atomic/atoms/button/button.styled'
import type { RadixIcon } from 'components/atomic/atoms/icon'
import { Spinner as SpinnerIcon } from 'components/atomic/atoms/icons/spinner'
import { LogoProps } from 'components/atomic/atoms/logo/logo-squared'

const NAME = 'Button'

type IconType =
  | RadixIcon
  | ((props: SVGProps<SVGSVGElement>) => JSX.Element)
  | ((props: LogoProps) => JSX.Element)

type ButtonVariants = VariantProps<typeof ButtonStyled>
type ButtonBaseProps = ComponentProps<typeof ButtonStyled>
type ButtonCustomProps = {
  icon?: IconType
  isLoading?: boolean
}

export type ButtonProps = ButtonCustomProps &
  ButtonBaseProps &
  ButtonVariants &
  CSSProps

const Button = forwardRef<ElementRef<typeof ButtonStyled>, ButtonProps>(
  ({ children, icon, isLoading, disabled, ...rest }, ref) => {
    const hasIcon = !!icon
    const hasText = !!children
    return (
      <ButtonStyled
        ref={ref}
        hasText={hasText}
        disabled={disabled || isLoading}
        aria-label={typeof children === 'string' ? children : 'button'}
        {...rest}
      >
        {hasIcon && !isLoading && (
          <ButtonLeftElement hasText={hasText}>
            <ButtonIcon as={icon} size={rest.size} />
          </ButtonLeftElement>
        )}
        {hasIcon && isLoading && (
          <ButtonLeftElement role="alert" aria-busy="true" hasText={hasText}>
            <ButtonIcon as={SpinnerIcon} size={rest.size} />
            <ButtonLoadingLabel>Loading...</ButtonLoadingLabel>
          </ButtonLeftElement>
        )}
        {!hasIcon && isLoading && (
          <ButtonCenterElement role="alert" aria-busy="true">
            <ButtonIcon as={SpinnerIcon} size={rest.size} />
            <ButtonLoadingLabel>Loading...</ButtonLoadingLabel>
          </ButtonCenterElement>
        )}

        {children}
      </ButtonStyled>
    )
  },
)

Button.defaultProps = {
  type: 'button',
}

Button.displayName = NAME

export default Button
